import { useState, useRef, useCallback, useEffect } from 'react';
import { Loader2, Link2, Check, ChevronDown, Link2Off, Paperclip, Plus, Settings } from 'lucide-react';
import { cn } from '@/lib/utils';
import { useBrowserStore } from '@/stores/browserStore';

interface MessageInputProps {
  onSend: (content: string) => void;
  onAction?: (action: string) => void;
  disabled?: boolean;
  isConnected?: boolean;
  isConnecting?: boolean;
  /** Hide the Zoom-API connection chip (browser mode shows it in the top
   *  status row instead — one status surface, not two corners). */
  hideConnectionChip?: boolean;
  /** Hide the browser-panel toggle (the guided-setup chat forces the panel
   *  open, so the switch is dead weight there). */
  hideBrowserToggle?: boolean;
  /** Override the composer placeholder (e.g. "ZAC is driving — take over
   *  anytime" while the agent controls the harness). */
  placeholderOverride?: string;
  /** Attach a requirements file (PDF, markdown, text): ZAC reads it into a plan. */
  onAttach?: (file: File) => void;
}

export function MessageInput({ onSend, onAction, disabled, isConnected, isConnecting, hideConnectionChip, hideBrowserToggle, placeholderOverride, onAttach }: MessageInputProps) {
  const browserPanelOpen = useBrowserStore((s) => s.panelOpen);
  const openBrowserPanel = useBrowserStore((s) => s.openPanel);
  const closeBrowserPanel = useBrowserStore((s) => s.closePanel);
  const [content, setContent] = useState('');
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const textareaRef = useRef<HTMLTextAreaElement>(null);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const fileRef = useRef<HTMLInputElement>(null);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsDropdownOpen(false);
      }
    };

    if (isDropdownOpen) {
      document.addEventListener('mousedown', handleClickOutside);
      return () => document.removeEventListener('mousedown', handleClickOutside);
    }
  }, [isDropdownOpen]);

  const handleSubmit = useCallback(
    (e?: React.FormEvent) => {
      e?.preventDefault();
      const trimmedContent = content.trim();
      if (!trimmedContent) return;
      if (disabled) return;

      onSend(trimmedContent);
      setContent('');

      // Reset textarea height
      if (textareaRef.current) {
        textareaRef.current.style.height = 'auto';
      }
    },
    [content, disabled, onSend]
  );

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSubmit();
    }
  };

  const handleTextareaChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    setContent(e.target.value);

    // Auto-resize textarea
    const textarea = e.target;
    textarea.style.height = 'auto';
    textarea.style.height = `${Math.min(textarea.scrollHeight, 200)}px`;
  };

  return (
    <form onSubmit={handleSubmit} className="relative">
      {/* Input area */}
      <div className="rounded-xl border border-white/10 bg-[hsl(230,15%,18%)] p-3 shadow-lg focus-within:border-primary focus-within:ring-1 focus-within:ring-primary">
        {/* Textarea */}
        <textarea
          ref={textareaRef}
          value={content}
          onChange={handleTextareaChange}
          onKeyDown={handleKeyDown}
          placeholder={placeholderOverride ?? 'Ask anything'}
          disabled={disabled}
          rows={1}
          className={cn(
            'max-h-[200px] min-h-[36px] w-full resize-none bg-transparent px-2 py-2 text-sm text-foreground placeholder:text-muted-foreground focus:outline-none',
            disabled && 'text-muted-foreground'
          )}
        />
        {disabled && (
          <div className="flex justify-end px-2 pb-1">
            <Loader2 className="h-4 w-4 animate-spin text-muted-foreground" />
          </div>
        )}

        {/* Quick actions row */}
        <div className="mt-2 flex items-center gap-2">
          {onAttach && (
            <>
              <input
                ref={fileRef}
                type="file"
                accept=".pdf,.md,.markdown,.txt"
                className="hidden"
                onChange={(e) => {
                  const file = e.target.files?.[0];
                  e.target.value = '';
                  if (file) onAttach(file);
                }}
              />
              <button
                type="button"
                onClick={() => fileRef.current?.click()}
                disabled={disabled}
                title="Attach a requirements document (PDF, markdown, text)"
                aria-label="Attach a document"
                className="flex items-center rounded-full bg-white/5 p-1.5 text-gray-300 transition-colors hover:bg-white/10 disabled:opacity-50"
              >
                <Paperclip className="h-3.5 w-3.5" />
              </button>
            </>
          )}
          {hideConnectionChip ? null : isConnected ? (
            /* Connected: Show dropdown menu */
            <div className="relative" ref={dropdownRef}>
              <button
                type="button"
                onClick={() => setIsDropdownOpen(!isDropdownOpen)}
                disabled={disabled || isConnecting}
                className="flex items-center gap-1.5 rounded-full bg-green-900/30 px-3 py-1.5 text-xs text-green-400 transition-colors hover:bg-green-900/40"
              >
                {isConnecting ? (
                  <Loader2 className="h-3.5 w-3.5 animate-spin" />
                ) : (
                  <Check className="h-3.5 w-3.5" />
                )}
                {isConnecting ? 'Connecting...' : 'Zoom API Connected'}
                <ChevronDown className={cn("h-3 w-3 transition-transform", isDropdownOpen && "rotate-180")} />
              </button>

              {isDropdownOpen && (
                <div className="absolute bottom-full left-0 mb-1 w-48 rounded-lg border border-border bg-card py-1 shadow-xl">
                  <button
                    type="button"
                    onClick={() => {
                      setIsDropdownOpen(false);
                      onAction?.('disconnect-zoom');
                    }}
                    className="flex w-full items-center gap-2 px-3 py-2 text-sm text-muted-foreground hover:bg-secondary hover:text-foreground"
                  >
                    <Link2Off className="h-4 w-4" />
                    Disconnect
                  </button>
                  <button
                    type="button"
                    onClick={() => {
                      setIsDropdownOpen(false);
                      onAction?.('add-account');
                    }}
                    className="flex w-full items-center gap-2 px-3 py-2 text-sm text-muted-foreground hover:bg-secondary hover:text-foreground"
                  >
                    <Plus className="h-4 w-4" />
                    Add Account
                  </button>
                  <button
                    type="button"
                    onClick={() => {
                      setIsDropdownOpen(false);
                      onAction?.('manage-accounts');
                    }}
                    className="flex w-full items-center gap-2 px-3 py-2 text-sm text-muted-foreground hover:bg-secondary hover:text-foreground"
                  >
                    <Settings className="h-4 w-4" />
                    Manage Accounts
                  </button>
                </div>
              )}
            </div>
          ) : (
            /* Not connected: Show connect button */
            <button
              type="button"
              onClick={() => onAction?.('connect-zoom')}
              disabled={disabled || isConnecting}
              className="flex items-center gap-1.5 rounded-full bg-white/5 px-3 py-1.5 text-xs text-gray-300 transition-colors hover:bg-white/10"
            >
              {isConnecting ? (
                <Loader2 className="h-3.5 w-3.5 animate-spin" />
              ) : (
                <Link2 className="h-3.5 w-3.5" />
              )}
              {isConnecting ? 'Connecting...' : 'Connect to Zoom'}
            </button>
          )}

          {/* Browser panel toggle */}
          {!hideBrowserToggle && (
          <button
            type="button"
            role="switch"
            aria-checked={browserPanelOpen}
            onClick={browserPanelOpen ? closeBrowserPanel : openBrowserPanel}
            className="flex items-center gap-2 rounded-full bg-white/5 px-3 py-1.5 text-xs text-gray-300 transition-colors hover:bg-white/10"
          >
            <span
              className={cn(
                "relative inline-flex h-4 w-7 shrink-0 items-center rounded-full transition-colors",
                browserPanelOpen ? "bg-blue-500" : "bg-white/20"
              )}
            >
              <span
                className={cn(
                  "inline-block h-3 w-3 transform rounded-full bg-white transition-transform",
                  browserPanelOpen ? "translate-x-3.5" : "translate-x-0.5"
                )}
              />
            </span>
            Show Browser Interface
          </button>
          )}
        </div>
      </div>

      {/* Hint */}
      <div className="mt-2 text-center text-xs text-muted-foreground">
        Press Enter to send, Shift+Enter for new line
      </div>
    </form>
  );
}
